import { Repeat2 } from 'lucide-react';
import { cn } from '@hqo/react-hooks';
import type { LucideIcon } from 'lucide-react';
import { ActivityItem } from 'app/data/types/activity';
import { ActivityStatusPill } from 'app/components/activity-status-pill';
import { ActivityTile } from './activity-tile';

interface CardScheduledHeadingProps {
  whenText: string;
  title: JSX.Element;
  pill: JSX.Element;
}

/** Scheduled item heading: the "when" line + pill on top, title beneath. */
const CardScheduledHeading = ({ whenText, title, pill }: CardScheduledHeadingProps): JSX.Element => (
  <div>
    <div className="flex items-center justify-between gap-2">
      <span className="truncate text-[12.5px] font-semibold text-system-grey-dark [font-variant-numeric:tabular-nums]">
        {whenText}
      </span>
      {pill}
    </div>
    <div className="mt-1.5">{title}</div>
  </div>
);

interface CardHeaderProps {
  item: ActivityItem;
  isOpen: boolean;
  /** "Today · 2:30 pm" — shown for scheduled (non-open) items. */
  whenText: string;
  where: string;
  /** The building's own icon for this vertical, resolved from its utility button. */
  Icon: LucideIcon;
}

/** Icon tile + the when/title/where block + status pill. */
export const CardHeader = ({ item, isOpen, whenText, where, Icon }: CardHeaderProps): JSX.Element => {
  const { details } = item;
  const pill = (
    <ActivityStatusPill
      statusKey={item.statusKey}
      type={item.type}
      statusLabel={details.statusLabel}
      statusColor={details.statusColor}
    />
  );
  const title = (
    <span className="text-[15.5px] font-semibold leading-snug tracking-[-0.01em] text-foreground">{details.title}</span>
  );

  return (
    <div className="flex items-start gap-3">
      <ActivityTile item={item} Icon={Icon} />

      <div className="min-w-0 flex-1">
        {isOpen ? (
          <div className="flex items-start justify-between gap-2">
            {title}
            {pill}
          </div>
        ) : (
          <CardScheduledHeading whenText={whenText} title={title} pill={pill} />
        )}

        {where && (
          <div className={cn('flex items-center gap-1.5', isOpen ? 'mt-1' : 'mt-0.5')}>
            <span className="truncate text-[13px] text-system-grey-dark">{where}</span>
            {details.flags?.recurring && (
              <Repeat2 size={13} strokeWidth={2} className="shrink-0 text-system-grey-dark-alt" aria-hidden />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
